import React from 'react';
import { ExpensesByPropertyReport, ReportPropertyOption } from './types';
import { LoadingBar } from '../../../components/common';

interface ReportsPanelProps {
  properties: ReportPropertyOption[];
  years: number[];
  selectedPropertyId: number | null;
  onSelectProperty: (propertyId: number | null) => void;
  selectedYear: number | null;
  onSelectYear: (year: number | null) => void;
  onSubmit: () => void;
  onDownloadHtml: () => void;
  reportResult: ExpensesByPropertyReport | null;
  error: string | null;
  globalLoading: boolean;
  loadingMessage: string;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

const ReportsPanel: React.FC<ReportsPanelProps> = ({
  properties,
  years,
  selectedPropertyId,
  onSelectProperty,
  selectedYear,
  onSelectYear,
  onSubmit,
  onDownloadHtml,
  reportResult,
  error,
  globalLoading,
  loadingMessage
}) => {
  return (
    <>
      <div>
        <h2>Expenses by Property</h2>
        <p>Select property and year to generate aggregate expense totals by category.</p>

        <div className="app-form-group">
          <label className="app-form-label required" htmlFor="report-property-select">Property</label>
          <select
            id="report-property-select"
            className="app-form-select"
            value={selectedPropertyId ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              onSelectProperty(value ? Number.parseInt(value, 10) : null);
            }}
          >
            <option value="">Select property...</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
              </option>
            ))}
          </select>
        </div>

        <div className="app-form-group">
          <label className="app-form-label required" htmlFor="report-year-select">Year</label>
          <select
            id="report-year-select"
            className="app-form-select"
            value={selectedYear ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              onSelectYear(value ? Number.parseInt(value, 10) : null);
            }}
            disabled={!selectedPropertyId || years.length === 0}
          >
            <option value="">Select year...</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div className="app-form-actions" style={{ justifyContent: 'flex-start' }}>
          <button
            className="app-btn app-btn-primary"
            onClick={onSubmit}
            disabled={!selectedPropertyId || !selectedYear}
          >
            Generate Report
          </button>
          <button
            className="app-btn app-btn-secondary"
            onClick={onDownloadHtml}
            disabled={!selectedPropertyId || !selectedYear || !reportResult}
          >
            Download HTML
          </button>
        </div>

        {error && <div className="app-error-message">{error}</div>}

        {reportResult && (
          <div style={{ marginTop: '1.5rem' }}>
            <h3>{reportResult.property.name} ({reportResult.year})</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.5rem' }}>Category</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.5rem' }}>Count</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.5rem' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {reportResult.categories.map((category) => (
                  <tr key={`${category.categoryId ?? 'none'}-${category.categoryName}`}>
                    <td style={{ borderBottom: '1px solid #e2e8f0', padding: '0.5rem' }}>{category.categoryName}</td>
                    <td style={{ borderBottom: '1px solid #e2e8f0', padding: '0.5rem' }}>{category.expenseCount}</td>
                    <td style={{ borderBottom: '1px solid #e2e8f0', padding: '0.5rem' }}>{formatCurrency(category.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: '0.5rem', fontWeight: 700 }}>Total</td>
                  <td style={{ padding: '0.5rem', fontWeight: 700 }}>{reportResult.totalExpenses}</td>
                  <td style={{ padding: '0.5rem', fontWeight: 700 }}>{formatCurrency(reportResult.totalAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      <LoadingBar isLoading={globalLoading} message={loadingMessage} />
    </>
  );
};

export default ReportsPanel;
